import { memo, useEffect, useRef, useState } from "react";
import queryString from "query-string";

import { ArrowLeft, Check, LayoutTemplate, LoaderCircle, PanelTop, SquareArrowOutUpRight } from "lucide-react";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";

import { useAppSelector, useAppDispatch, selectSettingsPage, openCoverPicker } from "utils/store";

import { TemplatePageType, kToolbarHeight } from "models";
import { saveTemplate } from "utils/api";
import { getAuthToken } from "utils/helpers/session";

const MemoButton = memo(Button);

export const TemplateToolbar = () => {
  const [showSave, setShowSave] = useState(false);
  const dispatch = useAppDispatch();
  const isSaving = useAppSelector((state) => state.templateBuilderSlice.isSaving);
  const selectedPageType = useAppSelector(
    (state) => state.templateBuilderSlice.pages[state.templateBuilderSlice.selectedPageIndex]?.type
  );
  const isCoverPageSelected = selectedPageType === TemplatePageType.coverPage;
  const prevIsSaving = useRef(false);

  useEffect(() => {
    if (prevIsSaving.current && !isSaving) {
      setShowSave(true);
      const timer = setTimeout(() => setShowSave(false), 3000);
      return () => clearTimeout(timer);
    }
    prevIsSaving.current = isSaving;
  }, [isSaving]);

  const closeEditorCallback = () => {
    window.top?.postMessage(JSON.stringify({ action: "close" }), "*");
  };

  const openPreview = async () => {
    dispatch(saveTemplate()).then(() => {
      const params = queryString.parse(window.location.search);
      const tenantGuid = params.tguid ?? "";
      // TODO: templateId will become a guid in a later phase.
      const templateId = params.rid ?? "";
      // Use the live token (which may have been refreshed mid-session) rather than
      // the stale ?token= the editor was opened with — the preview popup is a
      // top-level window and can't run the parent refresh handshake itself.
      const token = getAuthToken() || params.token || "";
      const windowName = "dms_preview_window";
      window.open(`preview?tguid=${tenantGuid}&rid=${templateId}&token=${token}`, windowName);
    });
  };

  const start = (
    <div className="flex flex-row items-center gap-4">
      <MemoButton
        className="p-0"
        rounded
        size="small"
        text={true}
        severity="secondary"
        aria-label="Close Editor"
        tooltip="Close Editor"
        onClick={closeEditorCallback}
      >
        <ArrowLeft size={20} />
      </MemoButton>

      {/* <ReportTitleInput /> */}
      <span className="font-bold color-[var(--primary-500)]" style={{ fontSize: "1.1rem" }}>Report Designer</span>
    </div>
  );

  const end = (
    <div className="flex flex-row items-center mr-4" style={{ color: "var(--green-800)" }}>
      {!isSaving && (
        <div
          className="inline-block mr-4"
          style={{ opacity: showSave && !isSaving ? 1 : 0, transition: "opacity 0.2s ease-in-out" }}
        >
          <div className="flex">
            <Check className="mr-1" size={20} strokeWidth={3} />
            <div className="mr-2">
              <b>Saved</b>
            </div>
          </div>
        </div>
      )}

      {isSaving && (
        <div className="inline-block mr-4">
          <div
            style={{
              animation: "spin 2s infinite linear",
              opacity: isSaving ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
            }}
          >
            <LoaderCircle size={30} color="var(--primary-200)" />
          </div>
        </div>
      )}

      {isCoverPageSelected && (
        <MemoButton
          label="Cover Page Designs"
          tooltip="Change the cover page design"
          data-pr-position="left"
          aria-label="Cover Design"
          severity="info"
          outlined
          size="small"
          className="mr-4"
          onClick={() => dispatch(openCoverPicker())}
        >
          <span className="ml-2"></span>
          <LayoutTemplate size={18} />
        </MemoButton>
      )}

      <MemoButton
        label="Header & Footer"
        tooltip="Edit the header & footer shown on every page after the cover"
        data-pr-position="left"
        aria-label="Header & Footer"
        severity="info"
        outlined
        size="small"
        className="mr-4"
        onClick={() => dispatch(selectSettingsPage())}
      >
        <span className="ml-2"></span>
        <PanelTop size={18} />
      </MemoButton>

      <MemoButton
        label="Preview"
        tooltip="Preview Template"
        data-pr-position="left"
        aria-label="Preview Template"
        severity="info"
        size="small"
        className="mr-4"
        disabled={isSaving}
        onClick={openPreview}
      >
        <span className="ml-2"></span>
        <SquareArrowOutUpRight size={18} />
      </MemoButton>
    </div>
  );

  return <Toolbar className="p-0 pl-4" style={{ height: `${kToolbarHeight}px` }} start={start} end={end}></Toolbar>;
};
